import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { ShieldCheck, ShieldOff, Check, AlertTriangle } from "lucide-react";
import api, { extractAxiosError } from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";

/** UI states for the 2FA section.
 *  - idle: shows current state + primary CTA (enable or disable)
 *  - enable_pending: code sent, waiting for user to type it
 *  - disable_pending: password prompt before disabling
 */
export default function TwoFactorSection() {
  const { t } = useTranslation();
  const { user, refresh } = useAuth();
  const enabled = !!user?.twofa_enabled;

  const [mode, setMode] = useState("idle");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [challengeToken, setChallengeToken] = useState("");
  const [code, setCode] = useState("");
  const [pwd, setPwd] = useState("");

  useEffect(() => {
    // Reset transient UI when user object changes (e.g. after toggle)
    setError("");
    setInfo("");
    setCode("");
    setPwd("");
  }, [enabled]);

  const startEnable = async () => {
    setBusy(true); setError(""); setInfo("");
    try {
      const { data } = await api.post("/auth/2fa/enable");
      setChallengeToken(data.challenge_token);
      setMode("enable_pending");
      setInfo(t("settings.twofaCodeSent"));
      // Dev disclose path: if backend reveals code (preview env only)
      if (data.dev_code) setInfo(`${t("settings.twofaCodeSent")} (dev_code=${data.dev_code})`);
    } catch (e) {
      setError(extractAxiosError(e, t("auth.resetErrorGeneric")));
    } finally { setBusy(false); }
  };

  const verifyEnable = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      await api.post("/auth/2fa/verify-enable", { challenge_token: challengeToken, code });
      await refresh();
      setMode("idle");
    } catch (err) {
      setError(extractAxiosError(err, t("settings.twofaInvalidCode")));
    } finally { setBusy(false); }
  };

  const disable = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      await api.post("/auth/2fa/disable", { password: pwd });
      await refresh();
      setMode("idle");
    } catch (err) {
      setError(extractAxiosError(err, t("settings.twofaWrongPassword")));
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-3" data-testid="twofa-section">
      <div className="flex items-center gap-2 text-sm">
        {enabled ? (
          <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded bg-emerald-500/10 border border-emerald-500/40 text-emerald-300 text-xs" data-testid="twofa-status-on">
            <Check size={12} /> {t("settings.twofaOn")}
          </span>
        ) : (
          <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded bg-slate-800 border border-slate-700 text-slate-400 text-xs" data-testid="twofa-status-off">
            {t("settings.twofaOff")}
          </span>
        )}
        <span className="text-xs text-slate-500">{t("settings.twofaExplain")}</span>
      </div>

      {mode === "idle" && !enabled && (
        <button
          onClick={startEnable}
          disabled={busy}
          className="px-4 py-2 rounded-md bg-blue-600 hover:bg-blue-500 text-white text-sm flex items-center gap-2 disabled:opacity-50"
          data-testid="twofa-enable-btn"
        >
          <ShieldCheck size={14} /> {busy ? t("auth.sending") : t("settings.twofaEnable")}
        </button>
      )}

      {mode === "idle" && enabled && (
        <button
          onClick={() => setMode("disable_pending")}
          className="px-4 py-2 rounded-md bg-red-600/10 hover:bg-red-600/20 text-red-300 border border-red-500/40 text-sm flex items-center gap-2"
          data-testid="twofa-disable-btn"
        >
          <ShieldOff size={14} /> {t("settings.twofaDisable")}
        </button>
      )}

      {mode === "enable_pending" && (
        <form onSubmit={verifyEnable} className="space-y-2 max-w-sm" data-testid="twofa-verify-form">
          {info && (
            <div className="px-3 py-2 rounded bg-blue-500/10 border border-blue-500/40 text-blue-200 text-xs" data-testid="twofa-info">{info}</div>
          )}
          <label className="label-tech block mb-1">{t("settings.twofaCodeLabel")}</label>
          <input
            type="text"
            inputMode="numeric"
            pattern="[0-9]{6}"
            maxLength={6}
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
            className="w-full px-4 py-2 rounded-md bg-slate-900 border border-slate-700 text-slate-100 text-lg font-mono tracking-[0.4em] text-center focus:outline-none focus:border-blue-500"
            placeholder="••••••"
            required
            data-testid="twofa-code-input"
          />
          {error && (
            <div className="px-3 py-2 rounded bg-red-500/10 border border-red-500/40 text-red-300 text-xs flex items-center gap-1.5" data-testid="twofa-error">
              <AlertTriangle size={12} /> {error}
            </div>
          )}
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => { setMode("idle"); setCode(""); setError(""); }}
              className="px-3 py-2 rounded-md bg-slate-800 hover:bg-slate-700 text-slate-200 text-xs border border-slate-700"
              data-testid="twofa-cancel-btn"
            >
              {t("settings.deleteCancel")}
            </button>
            <button
              type="submit"
              disabled={busy || code.length !== 6}
              className="px-4 py-2 rounded-md bg-emerald-600 hover:bg-emerald-500 text-white text-sm flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="twofa-verify-submit"
            >
              <Check size={14} /> {busy ? t("auth.savingPassword") : t("settings.twofaConfirm")}
            </button>
          </div>
        </form>
      )}

      {mode === "disable_pending" && (
        <form onSubmit={disable} className="space-y-2 max-w-sm" data-testid="twofa-disable-form">
          <label className="label-tech block mb-1">{t("settings.twofaConfirmPassword")}</label>
          <input
            type="password"
            value={pwd}
            onChange={(e) => setPwd(e.target.value)}
            className="w-full px-4 py-2 rounded-md bg-slate-900 border border-slate-700 text-slate-100 text-sm focus:outline-none focus:border-red-500"
            required
            data-testid="twofa-disable-pwd"
          />
          {error && (
            <div className="px-3 py-2 rounded bg-red-500/10 border border-red-500/40 text-red-300 text-xs">{error}</div>
          )}
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => { setMode("idle"); setPwd(""); setError(""); }}
              className="px-3 py-2 rounded-md bg-slate-800 hover:bg-slate-700 text-slate-200 text-xs border border-slate-700"
              data-testid="twofa-disable-cancel"
            >
              {t("settings.deleteCancel")}
            </button>
            <button
              type="submit"
              disabled={busy || !pwd}
              className="px-4 py-2 rounded-md bg-red-600 hover:bg-red-500 text-white text-sm flex items-center gap-2 disabled:opacity-50"
              data-testid="twofa-disable-confirm"
            >
              <ShieldOff size={14} /> {t("settings.twofaDisableConfirm")}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}
